/**
 * Mailversand: Sperre und Postausgang.
 *
 * Der Sinn der Sperre steht auf der Seite selbst, nicht nur in der
 * Dokumentation: wer sie setzt, tut das meist unter Zeitdruck – vor einer
 * Datenübernahme – und soll dabei lesen können, was genau sie bewirkt.
 *
 * Der Postausgang gehört dazu und nicht auf eine eigene Seite. Eine Sperre
 * ohne Nachweis wäre eine Behauptung: nach einer Woche muss man sehen
 * können, was liegen geblieben ist, und den Text haben, um es nachzuholen.
 */

import { useState } from 'react'
import {
  VERSANDMODUSHINWEIS,
  VERSANDMODUSTEXT,
  VERSANDSTATUSTEXT,
  usePostausgang,
  usePostausgangText,
  useVersand,
  useVersandSetzen,
  type Versandmodus,
} from '../api/kundenakte'
import { Blatt } from '../bausteine/Blatt'
import { Fehlerhinweis, Ladeanzeige } from '../bausteine/Hinweise'

const MODI = Object.keys(VERSANDMODUSTEXT) as Versandmodus[]

function zeitpunktDeutsch(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function Mailversand() {
  const stand = useVersand()
  const setzen = useVersandSetzen()
  const [modus, setzeModus] = useState<Versandmodus | null>(null)
  const [ziel, setzeZiel] = useState<string | null>(null)
  const [nurZurueckgehalten, setzeNurZurueckgehalten] = useState(false)
  const [gewaehlt, setzeGewaehlt] = useState<string | null>(null)

  const liste = usePostausgang(nurZurueckgehalten)
  const einzeln = usePostausgangText(gewaehlt)

  if (stand.isLoading) return <Ladeanzeige />
  if (stand.error) return <Fehlerhinweis fehler={stand.error} />

  const daten = stand.data!
  const aktuellerModus = modus ?? daten.modus
  const aktuellesZiel = ziel ?? daten.umleitung_an ?? ''

  return (
    <div className="space-y-4">
      <section className={daten.eingeschraenkt ? 'karte border-stufe-2' : 'karte'}>
        <h2 className="text-xl font-bold">Versand</h2>

        {daten.eingeschraenkt && (
          <p className="mt-2 rounded-feld border-2 border-stufe-2 bg-stufe-2-grund p-3 font-semibold text-stufe-2">
            Der Versand ist eingeschränkt: {VERSANDMODUSTEXT[daten.modus]}.
            {daten.unterdrueckt_7_tage > 0 &&
              ` In den letzten sieben Tagen wurden ${daten.unterdrueckt_7_tage} Nachrichten zurückgehalten.`}
          </p>
        )}

        <form
          className="mt-3 space-y-3"
          onSubmit={(e) => {
            e.preventDefault()
            setzen.mutate(
              { modus: aktuellerModus, umleitung_an: aktuellesZiel },
              {
                onSuccess: () => {
                  setzeModus(null)
                  setzeZiel(null)
                },
              },
            )
          }}
        >
          <fieldset className="space-y-2">
            <legend className="mb-1 block font-semibold text-slate-800">Betriebsart</legend>
            {MODI.map((m) => (
              <label
                key={m}
                className={`flex cursor-pointer items-start gap-3 rounded-feld border-2 p-3 font-normal ${
                  aktuellerModus === m ? 'border-akzent-500 bg-akzent-grund' : 'border-slate-200'
                }`}
              >
                <input
                  type="radio"
                  name="versandmodus"
                  value={m}
                  checked={aktuellerModus === m}
                  onChange={() => setzeModus(m)}
                  className="mt-1 h-6 w-6 shrink-0"
                  style={{ minHeight: 'auto' }}
                />
                <span>
                  <span className="font-semibold">{VERSANDMODUSTEXT[m]}</span>
                  <span className="block text-base text-slate-700">
                    {VERSANDMODUSHINWEIS[m]}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>

          {aktuellerModus === 'umleitung' && (
            <div>
              <label htmlFor="umleitung">Alles geht an diese Adresse</label>
              <input
                id="umleitung"
                type="email"
                value={aktuellesZiel}
                onChange={(e) => setzeZiel(e.target.value)}
                placeholder="ku@brumm-webdesign.de"
                required
              />
              <p className="mt-1 text-base text-slate-600">
                Ohne Adresse wird nichts verschickt — eine Umleitung ins Leere
                wäre sonst stillschweigend derselbe Versand wie ohne Sperre.
              </p>
            </div>
          )}

          <Fehlerhinweis fehler={setzen.error} />

          <button type="submit" className="knopf-haupt" disabled={setzen.isPending}>
            {setzen.isPending ? 'Speichert …' : 'Übernehmen'}
          </button>
        </form>
      </section>

      <section>
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <h2 className="ueberschrift-akzent text-xl">Postausgang</h2>
          <label className="ml-auto flex items-center gap-2 font-normal">
            <input
              type="checkbox"
              checked={nurZurueckgehalten}
              onChange={(e) => setzeNurZurueckgehalten(e.target.checked)}
              className="h-6 w-6 shrink-0"
              style={{ minHeight: 'auto' }}
            />
            <span>Nur Zurückgehaltenes</span>
          </label>
        </div>

        <p className="mb-3 text-base text-slate-600">
          Die letzten 100 Vorgänge. Einträge werden nach 90 Tagen gelöscht — es
          ist ein Nachweis für die Zeit einer Sperre, kein Archiv.
        </p>

        {liste.isLoading && <Ladeanzeige />}
        <Fehlerhinweis fehler={liste.error} />

        {liste.data?.length === 0 ? (
          <p className="text-slate-600">
            {nurZurueckgehalten ? 'Nichts zurückgehalten.' : 'Noch nichts verschickt.'}
          </p>
        ) : (
          <ul className="space-y-2">
            {liste.data?.map((z) => (
              <li key={z.id}>
                <button
                  type="button"
                  className="karte-knopf text-left"
                  onClick={() => setzeGewaehlt(z.id)}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold">{z.betreff}</span>
                    <span className="block text-base text-slate-700">
                      an {z.an}
                      {z.tatsaechlich_an && z.tatsaechlich_an !== z.an
                        ? ` → ${z.tatsaechlich_an}`
                        : ''}{' '}
                      · {zeitpunktDeutsch(z.zeitpunkt)}
                    </span>
                    {z.fehler && (
                      <span className="block text-base text-stufe-3">{z.fehler}</span>
                    )}
                  </span>
                  <span
                    className={`marke ${
                      z.status === 'verschickt'
                        ? 'bg-stufe-ob-grund text-stufe-ob'
                        : z.status === 'gescheitert'
                          ? 'bg-stufe-3-grund text-stufe-3'
                          : 'bg-stufe-2-grund text-stufe-2'
                    }`}
                  >
                    {VERSANDSTATUSTEXT[z.status]}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {gewaehlt && (
        <Blatt
          titel={einzeln.data?.betreff ?? 'Nachricht'}
          untertitel={einzeln.data ? `an ${einzeln.data.an}` : undefined}
          schliessen={() => setzeGewaehlt(null)}
          breit
        >
          {einzeln.isLoading ? (
            <Ladeanzeige />
          ) : (
            <pre className="whitespace-pre-wrap font-sans text-base">
              {einzeln.data?.text}
            </pre>
          )}
        </Blatt>
      )}
    </div>
  )
}
